/** Follow-up toast helpers for task mutation hooks. */
import { useCallback } from 'react';
import { triggerFollowUpFlow } from '@/services/followUpService';
import { useToast } from '@/features/ui/hooks/useToast';
import { useT } from '@/i18n/useT';

/**
 * @param {{
 *   updateTask: Function,
 *   deleteTask: Function,
 *   handleToggleComplete: Function,
 *   handleFollowUpRequest: Function,
 *   moveTask: Function,
 *   rebalanceOrderKeys: Function,
 *   updateTaskDuration: Function,
 *   updateMultipleTasks: Function,
 *   handleStartFocus: Function,
 * }} handlers
 * @returns {TaskMutationsContextValue}
 */
export function toTaskMutationsContextValue(handlers) {
  return {
    updateTask: /** @type {TaskMutationsContextValue['updateTask']} */ (
      handlers.updateTask
    ),
    deleteTask: /** @type {TaskMutationsContextValue['deleteTask']} */ (
      handlers.deleteTask
    ),
    handleToggleComplete:
      /** @type {TaskMutationsContextValue['handleToggleComplete']} */ (
        handlers.handleToggleComplete
      ),
    triggerFollowUp:
      /** @type {TaskMutationsContextValue['triggerFollowUp']} */ (
        handlers.handleFollowUpRequest
      ),
    moveTask: /** @type {TaskMutationsContextValue['moveTask']} */ (
      handlers.moveTask
    ),
    rebalanceOrderKeys:
      /** @type {TaskMutationsContextValue['rebalanceOrderKeys']} */ (
        handlers.rebalanceOrderKeys
      ),
    updateTaskDuration:
      /** @type {TaskMutationsContextValue['updateTaskDuration']} */ (
        handlers.updateTaskDuration
      ),
    updateMultipleTasks:
      /** @type {TaskMutationsContextValue['updateMultipleTasks']} */ (
        handlers.updateMultipleTasks
      ),
    handleStartFocus:
      /** @type {TaskMutationsContextValue['handleStartFocus']} */ (
        handlers.handleStartFocus
      ),
  };
}

export const useFollowUpToast = () => {
  const { showToast } = useToast();
  const t = useT();

  const handleFollowUpRequest = useCallback(
    async (taskId, userId) => {
      try {
        await triggerFollowUpFlow(taskId, userId);
      } catch (error) {
        console.error('Failed to trigger follow-up flow:', error);
        showToast(
          t('useTaskActions.followUpError', 'Error scheduling follow-up'),
          { type: 'error' },
        );
      }
    },
    [showToast, t],
  );

  const showFollowUpToast = useCallback(
    (taskId, userId) => {
      showToast('Wachten op reactie?', {
        action: {
          label: 'Ja',
          onClick: () => handleFollowUpRequest(taskId, userId),
        },
      });
    },
    [showToast, handleFollowUpRequest],
  );

  return { handleFollowUpRequest, showFollowUpToast };
};
